"use client";

import { useDraggable } from "@dnd-kit/core";
import { LinkSlashIcon } from "@heroicons/react/20/solid";
import { useContext, useEffect, useId, useMemo, useRef } from "react";
import { useConnectAccountPoll } from "../hooks/useConnectAccountPoll";

import { usePublicationStatusKind } from "../hooks/usePublicationStatusKind";
import {
  getSocialAccountHandle,
  isAuthenticatedSocialProviderAccount,
  SocialProviderAccount,
  useSocialProviderAccounts,
} from "../hooks/useSocialProviderAccounts";
import { PublicationModel, SocialProviderType } from "../types/models";
import { getTimezoneShortLabel } from "../utils/date";
import { providerTypeToIconLabel } from "./AddSocialProviderAccountButton";
import Button from "./Button";
import AutoPostPublicationMark, { AUTO_POST_PUBLICATION_TOOLTIP } from "./autoPublish/AutoPostPublicationMark";
import { CalendarContext } from "./Calendar";
import CustomTooltip from "./CustomTooltip";
import {
  POST_NOW_FIRING_HINT,
  POST_NOW_FIRING_LABEL,
} from "./publicationFlow/postNow";
import { getPublicationStatusPresentation } from "./publicationFlow/publicationStatusPresentation";
import PublicationStatusIcon from "./PublicationStatusIcon";
import PublicationStatusTooltipContent from "./PublicationStatusTooltipContent";
import {
  formatScheduleDateTimeLabel,
  formatScheduleTimeLabel,
} from "./ScheduleDateLabel";
import Typography from "./Typography";

// Reconnect affordance when the account is gone. An authenticated account
// adds nothing to the trailing slot beside the status glyph: the tile's
// identity is the provider mark + handle line under the time.
function PublicationAccountFace({
  publication,
  disableTooltips = false,
}: {
  publication: PublicationModel;
  disableTooltips?: boolean;
}) {
  const {
    readAccounts: { data: accounts },
  } = useSocialProviderAccounts();
  const { connect } = useConnectAccountPoll();

  const account = accounts?.find(
    (account: SocialProviderAccount) => account.id === publication.accountId,
  );

  const handleReconnect = (e: React.MouseEvent) => {
    e.stopPropagation();
    connect(publication.provider as SocialProviderType, publication.accountId);
  };

  // No token at all, an id-only placeholder (the API could not reach the
  // provider with the stored token), or a token the provider rejected: in all
  // three cases the post will fail and only a reconnect fixes it. Previously
  // the last two rendered the raw account id with no way to act on it.
  if (!account || !isAuthenticatedSocialProviderAccount(account))
    return (
      <Button
        variant="flat"
        size="s"
        onClick={handleReconnect}
        className="z-10 shrink-0"
        data-tooltip-id={disableTooltips ? undefined : "global"}
        data-tooltip-content={`Reconnect your ${
          providerTypeToIconLabel[publication.provider as SocialProviderType]
            ?.label ?? publication.provider
        } account`}
      >
        <LinkSlashIcon className="w-4 h-4" /> Reconnect
      </Button>
    );

  return null;
}

// Combined Publication Item component that can be used both in drag overlay and as draggable
interface PublicationItemProps extends React.HTMLAttributes<HTMLDivElement> {
  publication: PublicationModel;
  // For both draggable and non-draggable
  onClick?: () => void;
  // Draggable specific props
  isDraggable?: boolean;
  isBeingDragged?: boolean;
  isTransitioning?: boolean;
  isInOverlay?: boolean;
  isDisabled?: boolean;
  // Ghost card previewing where a dragged publication would drop
  isDropPreview?: boolean;
  // The drag source's own slot is the hovered drop target: the source card
  // doubles as the drop preview (blue) instead of the dimmed origin ghost
  isOriginDropPreview?: boolean;
  // Reference callback for scrolling
  onRefSet?: (publicationId: string, element: HTMLElement | null) => void;
}

export default function PublicationItem({
  publication,
  isDraggable = false,
  isBeingDragged = false,
  onRefSet,
  isTransitioning = false,
  isInOverlay = false,
  isDisabled = false,
  isDropPreview = false,
  isOriginDropPreview = false,
  className,
  ...rest
}: PublicationItemProps) {
  const itemRef = useRef<HTMLDivElement>(null);
  const {
    onPublicationClick: calendarPublicationClick,
    getPublicationContext,
    highlightedAccountId,
    highlightedVideoId,
    highlightTrigger,
    isPublicationInActiveCalendarScope,
    showSavedScheduledStatus,
    activePublicationId,
    revealedPublicationId,
    revealPulseToken,
    isRecentlyDropped,
    draggingPublication,
  } = useContext(CalendarContext);
  const calendarStatusTooltipId = useId();
  const otherProjectTooltipId = useId();
  const {
    readAccounts: { data: accounts },
  } = useSocialProviderAccounts();
  const account = accounts?.find(
    (candidate: SocialProviderAccount) =>
      candidate.id === publication.accountId,
  );
  // The handle is what tells same-provider accounts apart (their avatars
  // were indistinguishable at tile size, which is why the avatar is gone).
  const accountHandle = account ? getSocialAccountHandle(account) : null;
  const providerMark =
    providerTypeToIconLabel[publication.provider as SocialProviderType];

  // Setup draggable attributes if component is draggable. The drop-preview
  // ghost must never register under the real publication id: dnd-kit keys
  // draggables by id, and a duplicate registration would shadow the real
  // card's data (including its actual scheduledAt) in drag events.
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: isDropPreview ? `${publication.id}-drop-preview` : publication.id,
    data: {
      publication,
      isDisabled,
      initialScheduledDate: publication.scheduledAt
        ? new Date(publication.scheduledAt)
        : new Date(),
    },
    disabled: !isDraggable || isDropPreview,
  });
  // In the calendar the lifted card leaves a ghost behind: the baseline tile,
  // dimmed, marking the slot it came from. It only turns into the blue drop
  // preview when its own slot is the hovered target.
  const isOriginGhost = isDragging && !isOriginDropPreview;
  const paintsAsDropPreview = isDropPreview || (isDragging && !isOriginGhost);
  // Unmount open tooltip instances as soon as a drag begins, including
  // tooltips on other cards the cursor crosses and the floating copy.
  const suppressCalendarTooltips = Boolean(
    draggingPublication || isDragging || isBeingDragged || isInOverlay,
  );

  // Format the time based on post status and hovering state
  let displayTime = "";

  if (publication.scheduledAt) {
    displayTime = formatScheduleTimeLabel(publication.scheduledAt);
  }

  const isHighlightedInCalendar =
    (highlightedVideoId !== null &&
      highlightedVideoId === publication.projectId) ||
    (highlightedAccountId !== null &&
      highlightedAccountId === publication.accountId);

  const hasRunHighlightBumpEffectRef = useRef(false);
  useEffect(() => {
    // Ephemeral instances (drag overlay, drop preview ghost) mount mid-drag;
    // replaying the highlight bump on them causes phantom animations.
    if (isInOverlay || isDropPreview) return;
    if (highlightTrigger === 0 || !isHighlightedInCalendar) return;

    // A card remounting because it was just drag-dropped into another slot is
    // a data shuffle, not a new highlight — don't replay the bump for it.
    const isMountRun = !hasRunHighlightBumpEffectRef.current;
    hasRunHighlightBumpEffectRef.current = true;
    if (isMountRun && isRecentlyDropped?.(publication.id)) return;

    const el = itemRef.current;
    if (!el) return;

    requestAnimationFrame(() => {
      el.classList.remove("animate-pub-bump");
      void el.offsetWidth;
      el.classList.add("animate-pub-bump");
    });
  }, [
    highlightTrigger,
    isHighlightedInCalendar,
    isInOverlay,
    isDropPreview,
    isRecentlyDropped,
    publication.id,
  ]);

  // The inspector's destination pulse: ONE tile, the one the calendar just
  // brought into view, saying "here it is" after the grid moved under a panel
  // that did not.
  //
  // Not the highlight bump above: `isHighlightedInCalendar` matches by
  // projectId OR accountId, so one trigger bumps every tile of that short or
  // that account across the grid — and `highlightTrigger` is inside the
  // scheduler's Calendar `key`, so bumping it would remount the whole grid and
  // detach the inspector's anchor. This channel names one tile.
  //
  // The class goes on through remove → forced reflow → add because
  // `.animate-pub-bump` is declared `forwards`: re-adding it to an element
  // that already carries it is a silent no-op.
  //
  // Deliberately NO mount guard here, unlike the highlight effect's
  // `hasRunHighlightBumpEffectRef`: a tile that mounts already matching this
  // token is exactly the cross-period destination we are pulsing — the
  // calendar only bumps the token after it has found the tile — and a mount
  // guard would swallow the one case the pulse exists for. No reduced-motion
  // check either: the calendar never bumps the token under reduced motion, so
  // that decision lives in one place.
  useEffect(() => {
    if (isInOverlay || isDropPreview) return;
    if (revealPulseToken === 0 || revealedPublicationId !== publication.id) {
      return;
    }
    const el = itemRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.classList.remove("animate-pub-bump");
      void el.offsetWidth;
      el.classList.add("animate-pub-bump");
    });
  }, [
    revealPulseToken,
    revealedPublicationId,
    publication.id,
    isInOverlay,
    isDropPreview,
  ]);

  const isCalendarOutOfScope =
    isPublicationInActiveCalendarScope != null &&
    !isPublicationInActiveCalendarScope(publication);
  const calendarContext =
    !isInOverlay && !isDropPreview && getPublicationContext
      ? getPublicationContext(publication)
      : null;
  const isOtherProjectContext =
    isCalendarOutOfScope && calendarContext?.kind === "other-project";

  // Selection follows the inspected post, not hover or the original anchor.
  // The panel stays stationary while paging, so this fill locates its current
  // post on the calendar. Same-short navigation can also inspect a sibling
  // whose account is outside the schedule's current selection.
  const isInspectedPost =
    !isInOverlay &&
    !isDropPreview &&
    activePublicationId != null &&
    activePublicationId === publication.id;

  const handlePublicationClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDisabled || isCalendarOutOfScope) return;
    calendarPublicationClick?.(publication, itemRef.current);
  };

  // Calendar tiles: [time][status] … [avatar]. In the schedule workspace a
  // saved scheduled post gets a calendar mark so it can be distinguished from
  // a newly staged post; the standalone calendar does not provide that rule.
  const isSavedScheduled = Boolean(showSavedScheduledStatus?.(publication));
  const statusKind = usePublicationStatusKind({
    status: publication.status,
    scheduledAt:
      !showSavedScheduledStatus || isSavedScheduled
        ? publication.scheduledAt
        : undefined,
  });
  const isFiring = statusKind === "queued";
  const postingTooltipKind =
    statusKind === "queued" || statusKind === "publishing" ? statusKind : null;
  const statusPresentation = statusKind
    ? {
        ...getPublicationStatusPresentation(statusKind),
        ...(isFiring
          ? { label: `${POST_NOW_FIRING_LABEL}. ${POST_NOW_FIRING_HINT}` }
          : {}),
      }
    : isSavedScheduled
      ? {
          kind: "scheduled" as const,
          label: "Already scheduled",
          iconClassName: "text-slate-500",
        }
      : null;
  const isNewlyStagedScheduled = Boolean(
    showSavedScheduledStatus != null &&
    publication.status === "scheduled" &&
    !isSavedScheduled,
  );
  const unifiedCalendarStatusLabel = isFiring
    ? POST_NOW_FIRING_LABEL
    : isSavedScheduled
      ? "Already scheduled"
      : statusPresentation?.kind === "published"
        ? "Posted"
        : null;
  const hasUnifiedCalendarStatusTooltip = Boolean(
    !suppressCalendarTooltips &&
    !isOtherProjectContext &&
    (postingTooltipKind ||
      ((unifiedCalendarStatusLabel || isNewlyStagedScheduled) &&
        publication.scheduledAt)),
  );
  // Posted is the ordinary outcome of a month, so it wears no glyph. The
  // time's tooltip still says "Posted". Glyphs stay for the states that
  // need attention: posting, verifying, queued, failed, and a saved slot.
  const calendarStatusIcon =
    statusPresentation && statusPresentation.kind !== "published" ? (
      <PublicationStatusIcon
        kind={statusPresentation.kind}
        label={statusPresentation.label}
        iconClassName={statusPresentation.iconClassName}
        size="sm"
        chrome={
          !suppressCalendarTooltips &&
          !hasUnifiedCalendarStatusTooltip &&
          !isOtherProjectContext
        }
      />
    ) : null;

  // The status tooltip anchors on the trailing status glyph; a tile with a
  // tooltip but no glyph (a newly staged post) anchors it on the time line.
  const statusTooltipAnchorProps = hasUnifiedCalendarStatusTooltip
    ? { tabIndex: 0, "data-tooltip-id": calendarStatusTooltipId }
    : {};
  const calendarTimeClassName = paintsAsDropPreview
    ? "text-white"
    : isInOverlay
      ? "text-blue-500"
      : "text-slate-700 dark:text-foreground-secondary";
  // The handle is the row's light secondary: it dims by opacity on the blue
  // drop-preview / overlay paints so it stays in the row's own colour there.
  const calendarHandleClassName =
    paintsAsDropPreview || isInOverlay ? "opacity-70" : "text-slate-500";

  // A tile sits on the grid with no edge of its own. The day lines are the
  // boxes. Hover and the open post match the queue column's video tile:
  // slate-50 under the pointer, slate-100 while that post is open.
  const calendarSurfaceClasses = () => {
    if (isInspectedPost) {
      return "bg-slate-100 transition-colors dark:bg-surface-tertiary";
    }
    return "bg-white transition-colors hover:bg-slate-50 dark:bg-surface dark:hover:bg-surface-secondary";
  };

  const getStatusClasses = () => {
    if (isInOverlay) {
      // The lifted card is the tile it came from, picked up: same white
      // surface, blue edge and shadow to say it is in the air.
      return "bg-white dark:bg-surface shadow-xl border border-blue-300 dark:border-blue-500/70 transform scale-105 rotate-1 animate-wiggle";
    }

    if (paintsAsDropPreview) {
      // Border colour, not just fill: the tile carries a border in the
      // calendar now, and a bare blue block would sit 2px shorter.
      return "!bg-blue-500 text-white border-blue-500";
    }

    if (isOriginGhost) {
      // The baseline tile at 40%: still the same white surface and border,
      // so the ghost reads as "this is where it was", not as a drop target.
      return "bg-white dark:bg-surface border-slate-200 dark:border-surface-tertiary opacity-40 pointer-events-none";
    }

    const statusClasses = {
      published: "",
      publishing: "pointer-events-none opacity-50",
      failed: "",
      scheduled: "",
      default: "",
    };

    const baseStatusClass =
      statusClasses[publication.status as keyof typeof statusClasses] ??
      statusClasses.default;

    // Status is spoken by the label row (colour, icon, wording) and, out of
    // scope, by the tier classes — the surface itself is status-neutral.
    return `${calendarSurfaceClasses()} ${baseStatusClass}`;
  };

  const style = `
    @keyframes wiggle {
      0% { transform: scale(1.05) rotate(1deg); }
      25% { transform: scale(1.05) rotate(-1deg); }
      50% { transform: scale(1.05) rotate(1deg); }
      75% { transform: scale(1.05) rotate(-1deg); }
      100% { transform: scale(1.05) rotate(1deg); }
    }
    .animate-wiggle {
      animation: wiggle 0.5s ease-in-out infinite;
    }
  `;

  // Combine props for draggable
  const draggableProps = isDraggable
    ? {
        ref: setNodeRef,
        ...attributes,
        ...listeners,
      }
    : isDraggable
      ? { ref: setNodeRef }
      : {};

  const isCalendarInScope =
    !isPublicationInActiveCalendarScope ||
    isPublicationInActiveCalendarScope(publication);
  // Three tiers in a host's calendar (the scheduler's tab): the batch is
  // the interactive tile; a same-project post outside the batch keeps the
  // dashed context tier; another project's post is quieter still and
  // explains itself on hover — cadence is account-global, so it can be
  // why this project's post is not on that day. The tile stays unnamed:
  // the project name is a whole extra line the compact tile has no room for,
  // and the tooltip already says which project it belongs to. Nothing
  // outside the batch is actionable: the click guard above and the host's
  // draggable rule enforce that explicitly, so the other-project tile
  // keeps pointer events for its tooltip.
  const calendarCardContainerTierClass = useMemo(() => {
    if (isInOverlay || isDragging || isDropPreview) return "";
    if (!isCalendarInScope) {
      if (isOtherProjectContext) {
        return "opacity-60 !cursor-default hover:!border-slate-200 dark:hover:!border-surface-tertiary";
      }
      return "opacity-[0.4] pointer-events-none cursor-not-allowed !bg-transparent dark:!bg-transparent !border !border-dashed !border-slate-300 dark:!border-surface-tertiary";
    }
    return "";
  }, [
    isCalendarInScope,
    isDragging,
    isDropPreview,
    isInOverlay,
    isOtherProjectContext,
  ]);

  const isCalendarActionable =
    !isInOverlay && !isDropPreview && !isDisabled && !isCalendarOutOfScope;
  const calendarLegacyDisabledChromeClass =
    isDisabled && !isCalendarOutOfScope ? "opacity-50 cursor-not-allowed" : "";

  // No resting border: the day grid is the frame. Padding is 4px around the
  // 40px thumbnail. The tile uses `rounded-lg` (8px), the thumbnail `rounded`
  // (4px).
  const tileBoxClass = "rounded-lg p-1";

  // Keyboard focus is a ring, deliberately: the inspector's active tile is
  // a tinted fill and nothing else, so the two never look alike and can
  // show at once (closing the popover hands focus back to the tile).
  const tileFocusClass =
    "outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ring-offset-white dark:ring-offset-surface";

  return (
    <>
      <style>{style}</style>
      <div
        {...draggableProps}
        {...rest}
        ref={(el) => {
          (itemRef as React.MutableRefObject<HTMLDivElement | null>).current =
            el;
          draggableProps.ref?.(el);
          onRefSet?.(publication.id, el);
        }}
        className={`group text-sm flex gap-1.5 items-center justify-start min-w-0 w-full ${tileBoxClass} relative cursor-pointer ${tileFocusClass} ${getStatusClasses()} ${calendarLegacyDisabledChromeClass} ${calendarCardContainerTierClass} ${className || ""}`}
        onClick={handlePublicationClick}
        // The tile's public handle. The pager no longer reaches for it —
        // CLOSING does, to hand focus back to the tile of the post that was on
        // screen — and the calendar's own scoped lookup falls back to it when
        // the ref map has not caught up.
        data-publication-id={publication.id}
        // One click target per tile, keyboard-reachable in the calendar;
        // an other-project tile is focusable only so its tooltip is.
        role={isCalendarActionable ? "button" : undefined}
        tabIndex={isCalendarActionable || isOtherProjectContext ? 0 : undefined}
        onKeyDown={
          isCalendarActionable
            ? (event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                handlePublicationClick(
                  event as unknown as React.MouseEvent<HTMLDivElement>,
                );
              }
            : undefined
        }
        data-tooltip-id={
          isOtherProjectContext && !suppressCalendarTooltips
            ? otherProjectTooltipId
            : undefined
        }
      >
        <div className="flex w-full items-center justify-start gap-2">
          <div className="relative h-10 w-auto !aspect-[9/16] shrink-0 overflow-hidden rounded">
            <img
              src={`${process.env.NEXT_PUBLIC_API_URL}/projects/${publication.projectId}/thumbnail`}
              alt={publication.postInfo?.title}
              className="object-cover h-full w-full bg-slate-200 dark:bg-surface-tertiary"
            />
          </div>
          {/* Thumbnail, then the time flush left. The provider mark sits on
              the handle's line, at the time's left edge, and the handle
              follows it. A long handle truncates. Posted has no check. */}
          <div className="flex min-w-0 flex-1 flex-col">
            {displayTime ? (
              <div className="flex items-center gap-1">
                <div
                  className={`w-max rounded outline-none focus-visible:ring-2 focus-visible:ring-ring ${calendarTimeClassName}`}
                  {...(calendarStatusIcon ? {} : statusTooltipAnchorProps)}
                >
                  <Typography
                    variant="button2"
                    component="span"
                    className="whitespace-nowrap leading-none dark:!font-normal"
                  >
                    {displayTime}
                  </Typography>
                </div>
                <AutoPostPublicationMark
                  autoFolderId={publication.autoFolderId}
                  disableTooltip={suppressCalendarTooltips || isOtherProjectContext}
                  className={paintsAsDropPreview ? "text-white" : undefined}
                />
              </div>
            ) : null}
            {providerMark || accountHandle ? (
              <div className="-mt-0.5 flex min-w-0 items-center gap-1">
                {providerMark ? (
                  <span
                    className="size-3 shrink-0 [&_svg]:block [&_svg]:size-full"
                    role="img"
                    aria-label={providerMark.label}
                  >
                    {providerMark.icon}
                  </span>
                ) : null}
                {accountHandle ? (
                  <Typography
                    variant="overline"
                    component="span"
                    className={`min-w-0 truncate leading-4 ${calendarHandleClassName}`}
                    title={accountHandle}
                  >
                    {accountHandle}
                  </Typography>
                ) : null}
              </div>
            ) : null}
          </div>
          {calendarStatusIcon ? (
            <span
              className="flex size-4 shrink-0 items-center justify-center rounded outline-none focus-visible:ring-2 focus-visible:ring-ring"
              {...statusTooltipAnchorProps}
            >
              {calendarStatusIcon}
            </span>
          ) : null}
          {hasUnifiedCalendarStatusTooltip ? (
            <CustomTooltip
              id={calendarStatusTooltipId}
              openEvents={{ mouseover: true, focus: true, click: true }}
              closeEvents={{ mouseout: true, blur: true }}
              globalCloseEvents={{
                escape: true,
                scroll: true,
                resize: true,
                clickOutsideAnchor: true,
              }}
            >
              {postingTooltipKind ? (
                <PublicationStatusTooltipContent kind={postingTooltipKind} />
              ) : (
                <span className="flex flex-col gap-0.5 whitespace-nowrap">
                  {unifiedCalendarStatusLabel ? (
                    <span className="font-medium text-tooltip-foreground">
                      {unifiedCalendarStatusLabel}
                    </span>
                  ) : null}
                  <span className="text-slate-400">
                    {formatScheduleDateTimeLabel(publication.scheduledAt!)},{" "}
                    {getTimezoneShortLabel(new Date(publication.scheduledAt!))}
                  </span>
                </span>
              )}
            </CustomTooltip>
          ) : null}
          <PublicationAccountFace
            publication={publication}
            disableTooltips={suppressCalendarTooltips}
          />
        </div>
      </div>
      {isOtherProjectContext && !suppressCalendarTooltips ? (
        <CustomTooltip
          id={otherProjectTooltipId}
          openEvents={{ mouseover: true, focus: true, click: true }}
          closeEvents={{ mouseout: true, blur: true }}
          globalCloseEvents={{
            escape: true,
            scroll: true,
            resize: true,
            clickOutsideAnchor: true,
          }}
        >
          <span className="flex flex-col gap-0.5 whitespace-nowrap">
            <span className="font-medium text-tooltip-foreground">
              {calendarContext?.label ?? "Other project"}
            </span>
            {publication.autoFolderId ? (
              <span>{AUTO_POST_PUBLICATION_TOOLTIP}</span>
            ) : null}
            <span className="text-slate-400">
              {publication.status === "published" ? "Posted" : "Scheduled"}
              {publication.scheduledAt
                ? ` · ${formatScheduleDateTimeLabel(publication.scheduledAt)}, ${getTimezoneShortLabel(new Date(publication.scheduledAt))}`
                : ""}
            </span>
          </span>
        </CustomTooltip>
      ) : null}
    </>
  );
}
